import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { TabPanel, TabView } from "primereact/tabview";
import { Toast } from "primereact/toast";
import LoaderTransparent from "../../components/ui/LoaderTransparent";
import { canDeleteUsers, isAdmin } from "../../utilities/permissions";
import {
  getEmpresaSsoId,
  getSsoAppName,
  getPerfilesPermitidos,
  usuariosApi,
} from "../../api/usuariosApi";
import { useGlobalContext } from "../../Config";

const LIST_FIELDS = [
  ["vendedoresPermitidos", "Vendedores permitidos"],
  ["canalesPermitidos", "Canales permitidos"],
  ["sucursalesPermitidas", "Sucursales permitidas"],
  ["centrosPermitidos", "Centros permitidos"],
  ["almacenesPermitidos", "Almacenes permitidos"],
  ["organizacionesVentaPermitidas", "Organizaciones de venta"],
  ["sectoresPermitidos", "Sectores permitidos"],
  ["listasPrecioPermitidas", "Listas de precio"],
];

const EMPTY_FORM = {
  id: null,
  nombre: "",
  apellido: "",
  usuario: "",
  contrasena: "",
  perfil: "",
  empresa: null,
  empresaId: null,
  vendedorCodigo: "",
  vendedoresPermitidos: "",
  canalesPermitidos: "",
  sucursalesPermitidas: "",
  centrosPermitidos: "",
  almacenesPermitidos: "",
  organizacionesVentaPermitidas: "",
  sectoresPermitidos: "",
  listasPrecioPermitidas: "",
  historia: false,
  verTodosLosVendedores: false,
  verTodosLosCanales: false,
  verTodasLasSucursales: false,
  verTodosLosCentros: false,
  verTodosLosAlmacenes: false,
};

const clean = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

const arrayText = (value) => (Array.isArray(value) ? value.join(", ") : "");

export default function Usuarios() {
  const toast = useRef(null);
  const { brand } = useGlobalContext();
  const empresaSso = getEmpresaSsoId(brand);
  const perfilActual = sessionStorage.getItem("perfil") || "";
  const perfiles = useMemo(
    () => getPerfilesPermitidos(perfilActual, brand),
    [perfilActual, brand],
  );
  const puedeEliminar = canDeleteUsers();
  const administradorGlobal = isAdmin();
  const empresaIdSesion =
    Number(sessionStorage.getItem("empresaActiva")) || null;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const cargar = async () => {
    try {
      setLoading(true);
      const data = await usuariosApi.listar(empresaSso);
      const filtrados = administradorGlobal
        ? data
        : data.filter((usuario) => usuario.perfil !== "ADMIN");
      setUsuarios(filtrados);
    } catch (error) {
      showError(error, "No fue posible cargar los usuarios SSO");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nuevo = () => {
    setForm({
      ...EMPTY_FORM,
      perfil: perfiles[0]?.value || "",
      empresa: empresaSso,
      empresaId: empresaIdSesion,
    });
    setDialogVisible(true);
  };

  const editar = (row) => {
    if (!administradorGlobal && row.perfil === "ADMIN") {
      showError(new Error("No tiene permisos para editar un ADMIN"));
      return;
    }

    setForm({
      ...EMPTY_FORM,
      ...row,
      contrasena: "",
      empresa: empresaSso,
      empresaId: Number(row.empresaId) || empresaIdSesion,
      vendedoresPermitidos: arrayText(row.vendedoresPermitidos),
      canalesPermitidos: arrayText(row.canalesPermitidos),
      sucursalesPermitidas: arrayText(row.sucursalesPermitidas),
      centrosPermitidos: arrayText(row.centrosPermitidos),
      almacenesPermitidos: arrayText(row.almacenesPermitidos),
      organizacionesVentaPermitidas: arrayText(
        row.organizacionesVentaPermitidas,
      ),
      sectoresPermitidos: arrayText(row.sectoresPermitidos),
      listasPrecioPermitidas: arrayText(row.listasPrecioPermitidas),
    });
    setDialogVisible(true);
  };

  const validar = () => {
    if (!clean(form.nombre)) throw new Error("Debe informar nombre");
    if (!clean(form.apellido)) throw new Error("Debe informar apellido");
    if (!clean(form.usuario)) throw new Error("Debe informar usuario/email");
    if (!form.id && !clean(form.contrasena)) {
      throw new Error("Debe informar contraseña al crear el usuario");
    }
    if (!form.perfil) throw new Error("Debe seleccionar perfil");
    if (!Number(form.empresa))
      throw new Error("No fue posible determinar la empresa SSO del usuario");
    if (!Number(form.empresaId))
      throw new Error("No fue posible determinar la empresa del usuario");
    if (
      ["VENDEDOR_PLANIFICADOR", "VENDEDOR_RETAIL", "VENDEDOR_MAYORISTA"].includes(form.perfil) &&
      !clean(form.vendedorCodigo)
    ) {
      throw new Error("Debe informar el código del vendedor");
    }
    if (perfilActual !== "ADMIN" && form.perfil === "ADMIN") {
      throw new Error("No puede asignar el perfil ADMIN");
    }
  };

  const guardar = async () => {
    try {
      validar();
      setSaving(true);
      if (form.id) await usuariosApi.editarPerfil(form.id, form);
      else await usuariosApi.crear(form);

      toast.current?.show({
        severity: "success",
        summary: "Éxito",
        detail: "Usuario guardado correctamente en SSO",
        life: 3500,
      });
      setDialogVisible(false);
      await cargar();
    } catch (error) {
      showError(error, "No fue posible guardar el usuario");
    } finally {
      setSaving(false);
    }
  };

  const eliminar = async (row) => {
    if (!puedeEliminar) return;
    if (!window.confirm(`¿Eliminar al usuario ${row.usuario}?`)) return;

    try {
      setLoading(true);
      await usuariosApi.eliminar(row.id);
      toast.current?.show({
        severity: "success",
        summary: "Éxito",
        detail: "Usuario eliminado correctamente",
        life: 3000,
      });
      await cargar();
    } catch (error) {
      showError(error, "No fue posible eliminar el usuario");
    } finally {
      setLoading(false);
    }
  };

  const showError = (error, fallback = "Ocurrió un error") => {
    const data = error?.response?.data;
    const detail =
      (typeof data === "string" && data) ||
      data?.mensaje ||
      data?.message ||
      error?.message ||
      fallback;
    toast.current?.show({
      severity: "error",
      summary: "Error",
      detail,
      life: 5000,
    });
  };

  const setField = (field, value) =>
    setForm((previous) => ({ ...previous, [field]: value }));

  const acciones = (row) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-pencil"
        rounded
        text
        tooltip="Editar"
        aria-label="Editar usuario"
        onClick={() => editar(row)}
      />
      {puedeEliminar && (
        <Button
          icon="pi pi-trash"
          rounded
          text
          severity="danger"
          tooltip="Eliminar"
          onClick={() => eliminar(row)}
        />
      )}
    </div>
  );

  const alcance = (row) => {
    if (["ADMIN", "ADMIN_PLANIFICADOR"].includes(row.perfil)) return "Total";
    if (["VENDEDOR_PLANIFICADOR", "VENDEDOR_RETAIL", "VENDEDOR_MAYORISTA"].includes(row.perfil)) {
      return row.vendedorCodigo || "Sin vendedor";
    }
    return `${row.vendedoresPermitidos?.length || 0} vendedor(es)`;
  };

  const tabla = (activos) => (
    <DataTable
      value={usuarios.filter((item) => item.activo === activos)}
      loading={loading}
      paginator
      rows={20}
      rowsPerPageOptions={[10, 20, 50, 100]}
      dataKey="id"
      size="small"
      stripedRows
      showGridlines
      filterDisplay="row"
      emptyMessage="No hay usuarios que mostrar"
      scrollable
      scrollHeight="calc(100vh - 19rem)"
    >
      <Column header="Acciones" body={acciones} style={{ width: "8rem" }} />
      <Column
        field="nombre"
        header="Nombre"
        sortable
        filter
        showFilterMenu={false}
      />
      <Column
        field="apellido"
        header="Apellido"
        sortable
        filter
        showFilterMenu={false}
      />
      <Column
        field="usuario"
        header="Usuario"
        sortable
        filter
        showFilterMenu={false}
      />
      <Column
        field="perfilDescripcion"
        header="Perfil"
        sortable
        filter
        showFilterMenu={false}
      />
      <Column header="Alcance" body={alcance} />
      <Column
        header="Expirada"
        body={(row) => (row.expirada ? "Sí" : "No")}
        style={{ width: "7rem" }}
      />
    </DataTable>
  );

  const esCoordinador = [
    "COORDINADOR_VENDEDOR",
    "COORDINADOR_MAYORISTA",
    "COORDINADOR_SIN_CANAL",
  ].includes(form.perfil);
  const esVendedor = [
    "VENDEDOR_PLANIFICADOR",
    "VENDEDOR_RETAIL",
    "VENDEDOR_MAYORISTA",
  ].includes(form.perfil);
  const esAlcance = esCoordinador || esVendedor;

  return (
    <div className="p-2">
      <Toast ref={toast} position="top-right" />
      <Card className="bg-blue-50 border-bottom-1 border-blue-200 py-2 px-3 mb-2">
        <div className="flex justify-content-between align-items-center">
          <div>
            <h3 className="m-0 text-blue-700">Mantenedor de Usuarios SSO</h3>
            <small className="text-600">Aplicación {getSsoAppName(empresaSso)}</small>
          </div>
          <Button
            label="Agregar"
            icon="pi pi-plus"
            onClick={nuevo}
            disabled={!perfiles.length}
          />
        </div>
      </Card>

      <Card style={{ position: "relative" }}>
        {loading && <LoaderTransparent coverParent overlayTint="transparent" />}
        <TabView>
          <TabPanel header="Activos">{tabla(true)}</TabPanel>
          <TabPanel header="Inactivos">{tabla(false)}</TabPanel>
        </TabView>
      </Card>

      <Dialog
        header={form.id ? "Editar usuario SSO" : "Agregar usuario SSO"}
        visible={dialogVisible}
        modal
        className="p-fluid custom-dialog"
        style={{ width: "min(58rem, 95vw)" }}
        onHide={() => setDialogVisible(false)}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Cancelar"
              icon="pi pi-times"
              text
              onClick={() => setDialogVisible(false)}
              disabled={saving}
            />
            <Button
              label="Guardar"
              icon="pi pi-save"
              loading={saving}
              onClick={guardar}
            />
          </div>
        }
      >
        <div className="grid pt-2">
          <Field label="Nombre *">
            <InputText
              value={form.nombre}
              onChange={(e) => setField("nombre", e.target.value)}
            />
          </Field>
          <Field label="Apellido *">
            <InputText
              value={form.apellido}
              onChange={(e) => setField("apellido", e.target.value)}
            />
          </Field>
          <Field label="Usuario / email *">
            <InputText
              value={form.usuario}
              onChange={(e) => setField("usuario", e.target.value)}
            />
          </Field>
          <Field label={form.id ? "Nueva contraseña" : "Contraseña *"}>
            <Password
              value={form.contrasena}
              onChange={(e) => setField("contrasena", e.target.value)}
              feedback={false}
              toggleMask
            />
          </Field>
          <Field label="Perfil *">
            <Dropdown
              value={form.perfil}
              options={perfiles}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setField("perfil", e.value)}
              placeholder="Seleccione perfil"
            />
          </Field>

          {esVendedor && (
            <Field label="Código vendedor *">
              <InputText
                value={form.vendedorCodigo}
                onChange={(e) => setField("vendedorCodigo", e.target.value)}
              />
            </Field>
          )}
          {esAlcance &&
            LIST_FIELDS.filter(([field]) =>
              esVendedor ? field !== "vendedoresPermitidos" : true,
            ).map(([field, label]) => (
              <Field key={field} label={label} full>
                <InputText
                  value={form[field]}
                  placeholder="Valores separados por coma"
                  onChange={(e) => setField(field, e.target.value)}
                />
              </Field>
            ))}

          {esCoordinador && (
            <div className="col-12">
              <div className="grid">
                <Flag
                  field="historia"
                  label="Acceso a historia"
                  form={form}
                  setField={setField}
                />
                <Flag
                  field="verTodosLosVendedores"
                  label="Todos los vendedores"
                  form={form}
                  setField={setField}
                />
                <Flag
                  field="verTodosLosCanales"
                  label="Todos los canales"
                  form={form}
                  setField={setField}
                />
                <Flag
                  field="verTodasLasSucursales"
                  label="Todas las sucursales"
                  form={form}
                  setField={setField}
                />
                <Flag
                  field="verTodosLosCentros"
                  label="Todos los centros"
                  form={form}
                  setField={setField}
                />
                <Flag
                  field="verTodosLosAlmacenes"
                  label="Todos los almacenes"
                  form={form}
                  setField={setField}
                />
              </div>
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
}

function Field({ label, children, full = false }) {
  return (
    <div className={full ? "field col-12" : "field col-12 md:col-6"}>
      <label className="font-medium block mb-2">{label}</label>
      {children}
    </div>
  );
}

function Flag({ field, label, form, setField }) {
  return (
    <div className="col-12 md:col-4 flex align-items-center gap-2">
      <Checkbox
        inputId={field}
        checked={form[field] === true}
        onChange={(e) => setField(field, e.checked)}
      />
      <label htmlFor={field}>{label}</label>
    </div>
  );
}
